/* eslint-disable no-case-declarations */
import { AnnouncementInfo, IncidentInfo, Alert } from '../AnnouncementInfo';
import produce from "immer";
import {
    AppState,
    Dialog,
    EventName,
    FileInfo,
    MainView,
    OperationState,
    RapView,
    SearchInfo,
    SearchInfoTarget,
    VSInstanceInfo,
    Fetchable,
    ActionRecorderState,
    NotificationInfo, NotificationType, ReleaseAction, FetchablePagedList, Fetchable_InProgress, Fetchable_Error, Fetchable_Success, VSForMacVersion, VectorSearchInfo
} from "./appState";
import { Actions, failureAction, HttpResponse, requestAction, successAction, SuspensionActionType, VoteType } from "./actions";
import { App, SignInState } from "../app";
import { SuggestQueryResults } from "../SuggestQueryResults";
import { SearchResult, SearchResults } from "../SearchResults";
import { PublicFeedback } from "../PublicFeedback";
import { ResourceStrings } from "../ResourceStrings";
import { UserProperties, ActivityResults } from "../UserProperties";
//import { remote } from "electron";
import { TagInfo } from "../TagInfo";
import { TagsResult } from "../TagsResult";
import { Minimark } from '../editor/Minimark';
import { stringFromErrorOrAny } from '../utils/tools';
import { UserRole } from '../config/permissions';
import { Operation, operationFailed, operationSubmitting, operationSuceeded } from './operation';
import { Action } from 'redux';
import { ModerationPost, User } from '../Moderation';
import { loadMeControl } from '../meCtrl';
import { beforeInstallTemplate } from '../constants/templates';
import { AISummary, ChildPost, getDefaultUserNames, hasSystemTag, isStateTag, PartialPost, PostAction, PostId, PostType, PrincipalId, ReactionCounts, ReactionType, RootPost, RootPostId, StateTag, SystemTags, Tag, TagCategory, Users } from '../RootPost';
import { isClosedState, stateFromStateTagId } from '../constants/feedback';
import { SpaceId } from '../config/spaces/spaceIds';
import { instance } from '../config/serverConfig';

// workaround for telemetry being slow - revisit in RC3
//import { TelemetryResult } from "./TelemetryHelper";
enum TelemetryResult {
    /**
     * Used for unknown or unavailable result.
     */
    None = 0,
    /**
     * A result without any failure from product or user.
     */
    Success = 1,
    /**
     * A result to indicate the action/operation failed because of product issue (not user faults)
     * Consider using FaultEvent to provide more details about the failure.
     */
    Failure = 2,
    /**
     * A result to indicate the action/operation failed because of user fault (e.g., invalid input).
     * Consider using FaultEvent to provide more details.
     */
    UserFault = 3,
    /**
     * A result to indicate the action/operation is cancelled by user.
     */
    UserCancel = 4,
}

const insignificantSysLogPatterns = [
    /.*\.tcdb/i,
    /.*remote_.*/i,
];

const FeedbackBotId = 7;

const rapMakeDirty = (draftState: AppState, whatChanged: string): void => {
    if (draftState.rapReportWhatsDirty.indexOf(whatChanged) === -1) {
        draftState.rapReportWhatsDirty.push(whatChanged);
    }
}

const rapClearDirty = (draftState: AppState, whatChanged: string): void => {
    if (draftState.rapReportWhatsDirty.indexOf(whatChanged) !== -1) {
        draftState.rapReportWhatsDirty = draftState.rapReportWhatsDirty.filter(x => x !== whatChanged);
    }
}

const rapSetDirty = (draftState: AppState, whatChanged: string, isDirty: boolean): void => {
    if (isDirty) {
        rapMakeDirty(draftState, whatChanged);
    } else {
        rapClearDirty(draftState, whatChanged);
    }
}

export const setContextualNotification = (draftState: AppState, key: string, title: string, isCritical: boolean = false, type: NotificationType = "information"): void => {
    if (key) {
        // eslint-disable-next-line security/detect-object-injection
        draftState.contextualNotifications[key] = [!title
            ? null
            : {
                title,
                type,
                isCritical
            } as NotificationInfo
        ];
    }
}

export const setContextualNotifications = (draftState: AppState, key: string, notificationInfos: NotificationInfo[]): void => {
    if (key) {
        // eslint-disable-next-line security/detect-object-injection
        draftState.contextualNotifications[key] = notificationInfos;
    }
}

export const ticketNotificationKey = (ticketId: number): string => `Ticket_${ticketId}`;

export const mainReducer = produce((draftState: AppState, action: Action<string>) => {
    App.tempSignInState = draftState.signInState;

    draftState.stateVersion++;
    draftState.lastAction = action.type;
    const actionAny = action as any;
    const notAvailable: VSForMacVersion = { ProductDisplayVersion: 'N/A' };
    notAvailable.ProductDisplayVersion = "N/A";
    let ticketId: number;

    switch (action.type) {
        case Actions.CLEAR_EVENTS:
            draftState.event = EventName.none;
            return;

        case Actions.SET_GLOBAL_NOTIFICATION:
            draftState.globalNotification = actionAny.notificationInfo;
            return;

        case Actions.SET_CONTEXTUAL_NOTIFICATION:
            setContextualNotifications(draftState, actionAny.key, [actionAny.notificationInfo]);
            return;

        case Actions.CLEAR_ALL_CONTEXTUAL_NOTIFICATION:
            draftState.contextualNotifications = {};
            return;

        case successAction(Actions.SET_SITE_READONLY_SETTING):
            draftState.isInReadOnlyMode = actionAny.req.data === true;
            return;

        case Actions.SET_APP_SETTINGS:
            draftState.settings = (action as any).payload.appSettings;
            return;

        case Actions.SET_OPERATION_STATE:
            const { key, state, data: opData, message } = actionAny.payload;
            // eslint-disable-next-line security/detect-object-injection
            let opItem = draftState.operations[key];
            if (!opItem) {
                opItem = {
                    state: state ?? OperationState.NotStarted,
                } as Operation;
                // eslint-disable-next-line security/detect-object-injection
                draftState.operations[key] = opItem;
            } else {
                if (state !== undefined) {
                    opItem.state = state;
                }
            }
            if (opData !== undefined) {
                opItem.data = opData;
            }
            if (message !== undefined) {
                opItem.message = message;
            }
            return;

        case Actions.DISCONNECTED_FROM_NATIVE_PROCESS:
            draftState.disconnectedFromNativeProcess = actionAny.payload.isDisconnected;
            return;

        // For controlling whether we show or not the Close button on the ReportAProblem
        case Actions.SET_CLOSEABLE:
            draftState.rapCloseable = (action as any).value;
            return;

        case Actions.SET_SHOW_GUIDANCE:
            draftState.user_local_settings.showGuidance = (action as any).value;
            App.saveUserSettings();
            return;

        case Actions.SET_SORT:
            draftState.sort = (action as any).value;
            App.telemetry.postBusinessEvent("sort-clicked", {
                "sort": draftState.sort,
                "filter": draftState.filter,
                "typeFilter": draftState.typeFilter.toString(),
            });
            return;

        case Actions.SET_FILTER:
            draftState.filter = (action as any).value;
            App.telemetry.postBusinessEvent("filter-clicked", {
                "sort": draftState.sort,
                "filter": draftState.filter,
                "typeFilter": draftState.typeFilter.toString(),
            });
            return;

        case Actions.SET_TYPE_FILTER:
            draftState.typeFilter = (action as any).value;
            App.telemetry.postBusinessEvent("type-filter-clicked", {
                "sort": draftState.sort,
                "filter": draftState.filter,
                "typeFilter": draftState.typeFilter.toString(),
            });
            return;

        // ----- load problem in details view, currently: 2017/11/21: only after clicking it through "my activity"
        case Actions.LOAD_PROBLEM_REQUEST:
            draftState.problemLoadState = OperationState.Executing;
            return;

        case Actions.LOAD_PROBLEM_SUCCESS:
            // @TODO: Avoid these calls in reducer. Actions or components are better place for them
            App.telemetry.postUserTask("LoadProblem", TelemetryResult.Success);
            draftState.problemLoadState = OperationState.Success;
            return;

        case Actions.LOAD_PROBLEM_FAILURE:
            const loadProblemError = (action as any).error;
            App.telemetry.postUserTask("LoadProblem", TelemetryResult.Failure);

            draftState.problemLoadErrorMessage = (loadProblemError && loadProblemError.message) ? loadProblemError.message : loadProblemError;
            draftState.problemLoadState = OperationState.Failed;
            return;

        // Load ticket by id
        case requestAction(Actions.LOAD_TICKET):
            draftState.ticketsById[actionAny.postId] = {
                isFetching: true,
                hasErrors: false
            };
            return;

        case successAction(Actions.LOAD_TICKET):
            const ticket: RootPost = actionAny.req.data;
            computeRootPostData(ticket);
            draftState.ticketsById[ticket.id] = {
                isFetching: false,
                hasErrors: false,
                data: ticket
            };
            App.telemetry.postUserTask("LoadTicket", TelemetryResult.Success);
            return;

        case failureAction(Actions.LOAD_TICKET):
            draftState.ticketsById[actionAny.postId] = {
                isFetching: false,
                hasErrors: true,
                errors: [stringFromErrorOrAny(actionAny.errorMessage)]
            };
            App.telemetry.postUserTask("LoadTicket", TelemetryResult.Failure);
            App.telemetry.postCriticalFault("LoadTicket", actionAny.errorMessage, null, (actionAny.postId ?? "unknown").toString());
            return;

        // Regenerate summary for ticket
        case requestAction(Actions.REGENERATE_SUMMARY):
            draftState.summaries[actionAny.postId] = {
                isFetching: true,
                hasErrors: false
            };
            return;

        case successAction(Actions.REGENERATE_SUMMARY):
            {
                const postId: PostId = actionAny.postId;
                const data: AISummary = actionAny.req.data;

                if (data.error) {
                    draftState.summaries[postId] = {
                        isFetching: false,
                        hasErrors: true,
                        errors: [data.error]
                    };
                }
                else {
                    data.text = App.HtmlFromMarkdownIfNeeded(data.text);
                    draftState.summaries[postId] = {
                        isFetching: false,
                        hasErrors: false,
                        data
                    };
                }

                // update ticket with summary if needed
                const ticket: Fetchable<RootPost> | undefined = draftState.ticketsById[postId];
                if (ticket && !ticket.isFetching && !ticket.hasErrors && ticket.data) {
                    ticket.data.ai = { summary: data };
                }

                if (data.error) {
                    App.telemetry.postUserTask("RegenerateSummary", TelemetryResult.Failure);
                    App.telemetry.postCriticalFault("RegenerateSummary", actionAny.errorMessage, null, (postId ?? "unknown").toString());
                }
                else {
                    App.telemetry.postUserTask("RegenerateSummary", TelemetryResult.Success);
                }
                return;
            }

        case failureAction(Actions.REGENERATE_SUMMARY):
            draftState.summaries[actionAny.postId] = {
                isFetching: false,
                hasErrors: true,
                errors: [stringFromErrorOrAny(actionAny.errorMessage)]
            };
            App.telemetry.postUserTask("RegenerateSummary", TelemetryResult.Failure);
            App.telemetry.postCriticalFault("RegenerateSummary", actionAny.errorMessage, null, (actionAny.postId ?? "unknown").toString());
            return;

        // -- feature feedback
        case requestAction(Actions.SEND_FEATURE_FEEDBACK):
            draftState.featureFeedback = Fetchable_InProgress();
            return;

        case successAction(Actions.SEND_FEATURE_FEEDBACK):
            draftState.featureFeedback = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.SEND_FEATURE_FEEDBACK):
            draftState.featureFeedback = Fetchable_Error(actionAny.errorMessage);
            return;

        // Load ticket from descendant comment
        case requestAction(Actions.LOAD_TICKET_FROM_COMMENT):
            draftState.ticketsByCommentId[actionAny.__rest.data.descendantCommentId] = {
                isFetching: true,
                hasErrors: false
            };
            return;

        case successAction(Actions.LOAD_TICKET_FROM_COMMENT):
            const ticketFromComment: RootPost = actionAny.req.ticket;
            computeRootPostData(ticketFromComment);
            draftState.ticketsByCommentId[actionAny.req.descendantCommentId] = {
                isFetching: false,
                hasErrors: false,
                data: ticketFromComment
            };
            App.telemetry.postUserTask("LoadTicketFromComment", TelemetryResult.Success);
            return;

        case failureAction(Actions.LOAD_TICKET_FROM_COMMENT):
            draftState.ticketsByCommentId[actionAny.__rest.data.descendantCommentId] = {
                isFetching: false,
                hasErrors: true,
                errors: [actionAny.error]
            };
            App.telemetry.postUserTask("LoadTicketFromComment", TelemetryResult.Failure);
            App.telemetry.postCriticalFault("LoadTicketFromComment", actionAny.errorMessage, null, actionAny.__rest.data.descendantCommentId);
            return;

        // Follow ticket
        case requestAction(Actions.FOLLOW_TICKET):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.FOLLOW_TICKET):
            ticketId = actionAny.__rest.data.answerHubId;
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, actionAny.req.data === true && "You are now following this feedback.");
            // eslint-disable-next-line security/detect-object-injection
            if (draftState.ticketsById[ticketId]) {
                const ticket: Fetchable<RootPost> = draftState.ticketsById[ticketId];
                if (ticket.data) {
                    ticket.data.isFollowing = true;
                }
            }

            App.telemetry.postUserTask("FollowTicket", TelemetryResult.Success);
            return;

        case failureAction(Actions.FOLLOW_TICKET):
            operationFailed(draftState, actionAny.__rest.data.operationKey, "There was an error in submitting your request to follow this feedback");
            App.telemetry.postUserTask("FollowTicket", TelemetryResult.Failure);
            return;

        // Unfollow ticket
        case requestAction(Actions.UNFOLLOW_TICKET):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.UNFOLLOW_TICKET):
            ticketId = actionAny.__rest.data.answerHubId;
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, actionAny.req.data === false && "You have stopped following this feedback.");
            // eslint-disable-next-line security/detect-object-injection
            if (draftState.ticketsById[ticketId]) {
                const ticket: Fetchable<RootPost> = draftState.ticketsById[ticketId];
                if (ticket.data) {
                    ticket.data.isFollowing = false;
                }
            }

            App.telemetry.postUserTask("UnfollowTicket", TelemetryResult.Success);
            return;

        case failureAction(Actions.UNFOLLOW_TICKET):
            operationFailed(draftState, actionAny.__rest.data.operationKey, "There was an error in submitting your request to stop following this feedback");
            App.telemetry.postUserTask("UnfollowTicket", TelemetryResult.Failure);
            return;

        // flag for moderation
        case requestAction(Actions.FLAG_FOR_MODERATION):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.FLAG_FOR_MODERATION):
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, "This content has been successfully flagged for moderation.");
            App.telemetry.postUserTask("FlagForModeration", TelemetryResult.Success);
            return;

        case failureAction(Actions.FLAG_FOR_MODERATION):
            // @TODO:ForNewBackend: Ignore flagging failures until we have the ability to identify user who flagged this item with new backend
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, "This content has been successfully flagged for moderation.");

            // const errMsg = actionAny.error.response.status === 401
            //     ? "We're sorry, but either this item has already been flagged for moderation or you don't have sufficient permission to request it."
            //     : `Sorry we were unable to submit your request for moderation at this time. (${actionAny?.error?.response?.data})`;
            // operationFailed(draftState, actionAny.__rest.data.operationKey, errMsg);

            App.telemetry.postUserTask("FlagForModeration", TelemetryResult.Failure);
            return;

        // Convert node
        case requestAction(Actions.CONVERT_NODE):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.CONVERT_NODE):
            operationSuceeded(draftState, actionAny.operationKey, "The content was successfully converted but it might take some time for the updates to show up. Please refresh this page after some time.");
            App.telemetry.postUserTask("ConvertNode", TelemetryResult.Success);
            return;

        case failureAction(Actions.CONVERT_NODE):
            operationFailed(draftState, actionAny.operationKey, "There was an error in converting the content.");
            App.telemetry.postUserTask("ConvertNode", TelemetryResult.Failure);
            return;

        // Update ticket
        case requestAction(Actions.UPDATE_TICKET):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.UPDATE_TICKET):
            operationSuceeded(draftState, actionAny.operationKey, "The feedback details were successfully updated but it might take some time for the updates to show up. Please refresh this page after some time.");
            App.telemetry.postUserTask("UpdateTicket", TelemetryResult.Success);
            return;

        case failureAction(Actions.UPDATE_TICKET):
            operationFailed(draftState, actionAny.operationKey, "There was an error in updating feedback details.");
            App.telemetry.postUserTask("UpdateTicket", TelemetryResult.Failure);
            return;

        // update comment
        case requestAction(Actions.UPDATE_COMMENT):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.UPDATE_COMMENT):
            operationSuceeded(draftState, actionAny.operationKey, "The comment was successfully updated but it might take some time for the updates to show up. Please refresh this page after some time.");
            App.telemetry.postUserTask("UpdateComment", TelemetryResult.Success);
            return;

        case failureAction(Actions.UPDATE_COMMENT):
            operationFailed(draftState, actionAny.operationKey, "There was an error in updating comment.");
            App.telemetry.postUserTask("UpdateComment", TelemetryResult.Failure);
            return;

        // add solution
        case requestAction(Actions.ADD_SOLUTION):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.ADD_SOLUTION):
            operationSuceeded(draftState, actionAny.operationKey, `The ${actionAny.isWorkaround ? "workaround" : "solution"} was successfully submitted but it might take some time for the updates to show up. Please refresh this page after some time.`);
            App.telemetry.postUserTask("AddSolution", TelemetryResult.Success);
            return;

        case failureAction(Actions.ADD_SOLUTION):
            operationFailed(draftState, actionAny.operationKey, `There was an error in adding ${actionAny.isWorkaround ? "workaround" : "solution"}.`);
            App.telemetry.postUserTask("AddSolution", TelemetryResult.Failure);
            App.telemetry.postCriticalFault("AddSolution", actionAny.errorMessage, null, actionAny.ticketId);
            return;

        // update solution
        case requestAction(Actions.UPDATE_SOLUTION):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.UPDATE_SOLUTION):
            operationSuceeded(draftState, actionAny.operationKey, `The ${actionAny.isWorkaround ? "workaround" : "solution"} was successfully updated but it might take some time for the updates to show up. Please refresh this page after some time.`);
            App.telemetry.postUserTask("UpdateSolution", TelemetryResult.Success);
            return;

        case failureAction(Actions.UPDATE_SOLUTION):
            operationFailed(draftState, actionAny.operationKey, `There was an error in updating ${actionAny.isWorkaround ? "workaround" : "solution"}.`);
            App.telemetry.postUserTask("UpdateSolution", TelemetryResult.Failure);
            return;

        // Vote on node
        case requestAction(Actions.VOTE_ON_NODE):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.VOTE_ON_NODE):
            {
                const voteType: ReactionType = actionAny.voteType;
                const isCancelled: boolean = actionAny.isCancelled;
                const postId: PostId = actionAny.postId;
                const rootPostId: RootPostId = actionAny.rootPostId;

                operationSuceeded(
                    draftState,
                    actionAny.operationKey,
                    isCancelled
                        ? "Your vote was successfully cancelled."
                        : "Thank you for voting!"
                );

                markVoteForNode(rootPostId, postId, draftState, voteType, isCancelled);

                App.telemetry.postUserTask("VoteOnNode", TelemetryResult.Success);
                return;
            }

        case failureAction(Actions.VOTE_ON_NODE):
            operationFailed(
                draftState,
                actionAny.operationKey,
                (actionAny.voteType as VoteType) === "cancelvote"
                    ? "There was an error in cancelling your vote."
                    : "There was an error in submitting your vote."
            );
            App.telemetry.postUserTask("VoteOnNode", TelemetryResult.Failure);
            return;

        // Reactivate node
        case requestAction(Actions.REACTIVATE_NODE):
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case successAction(Actions.REACTIVATE_NODE):
            operationSuceeded(draftState, actionAny.operationKey, "Your request was successfully submitted but it might take some time for the updates to show up. Please refresh this page after some time.");
            App.telemetry.postUserTask("Reactivate", TelemetryResult.Success);
            return;

        case failureAction(Actions.REACTIVATE_NODE):
            operationFailed(draftState, actionAny.operationKey, "There was an error in submitting your request to reactivate.");
            App.telemetry.postUserTask("Reactivate", TelemetryResult.Failure);
            App.telemetry.postCriticalFault("Reactivate", actionAny.errorMessage, null, actionAny.ticketId);
            return;

        // Delete node
        case requestAction(Actions.DELETE_NODE):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.DELETE_NODE):
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, "The content was deleted succesfully it might take some time for the updates to show up. Please refresh this page after some time.");
            App.telemetry.postUserTask("DeleteNode", TelemetryResult.Success);
            return;

        case failureAction(Actions.DELETE_NODE):
            operationFailed(draftState, actionAny.__rest.data.operationKey, "There was an error in submitting your request to delete node.");
            App.telemetry.postUserTask("DeleteNode", TelemetryResult.Failure);
            return;

        case Actions.INIT_DETAILS_VIEW:
            const newItem: SearchResult | null = actionAny.item;
            const postId: PostId = actionAny.postId;

            if (draftState.selectedItem !== newItem && newItem != null) {
                draftState.selectedItem = actionAny.item;
                draftState.selectedItemId = actionAny.item.answerHubId;
                draftState.selectedCommentsExpanded = false;
            }

            draftState.problemLoadState = OperationState.Success;
            draftState.commentText = '';

            // When you add a vote from the EV (Enhanced Vote) dialog, we add a comment, refresh the problem and add a vote.
            // We need the reset so we put the proper vote state in selectProblem and not get into situations where you double vote
            draftState.voteState = OperationState.NotStarted;
            return;

        case Actions.TOGGLE_TOP_LEVEL_COMMENTS:
            draftState.selectedCommentsExpanded = !draftState.selectedCommentsExpanded;
            if (draftState.selectedCommentsExpanded) {
                App.telemetry.postUserTask("CommentExpanded", TelemetryResult.Success);
            }
            return;

        // -------- ADD_COMMENT
        case Actions.ADD_COMMENT_REQUEST:
            draftState.detailAddCommentState = OperationState.Executing;
            operationSubmitting(draftState, actionAny.operationKey);
            return;

        case Actions.ADD_COMMENT_SUCCESS:
            const addCommentItem: PartialPost = actionAny.item;

            operationSuceeded(draftState, actionAny.operationKey, "Your comment was successfully submitted but it might take some time for the updates to show up. Please refresh this page after some time.");

            draftState.detailAddCommentState = OperationState.Success;
            // if on the same item in details view, update the comments to include the just added one
            //(it takes a bit to be processed on the backend - added to DevCom, indexed, etc.)
            if (draftState.selectedItem?.id === addCommentItem.id) {
                const rootPost: RootPost = draftState.ticketsById[draftState.selectedItem?.id].data;
                addComment(rootPost, rootPost.currentUserId, draftState.userName, actionAny.data.comment);
            }
            draftState.commentText = "";
            draftState.selectedCommentsExpanded = true;
            App.telemetry.postUserTask(
                "AddComment",
                TelemetryResult.Success,
                undefined,
                addCommentItem.trackingId,
                {
                    "TotalTimeMsec": (Date.now() - actionAny.data.submissionStartTimeMsec).toString(),
                },
            );
            return;

        case Actions.ADD_COMMENT_FAILURE:
            draftState.detailAddCommentState = OperationState.Failed;
            operationFailed(draftState, actionAny.operationKey, "There was an error in submitting your comment.");
            App.telemetry.postCriticalFault("AddComment", actionAny.errorMessage, null, actionAny.item.trackingId);
            return;

        // -------- VOTE
        case Actions.VOTE_REQUEST:
            App.telemetry.postUserTask("VoteSubmitted", TelemetryResult.Success, "", actionAny.__rest.item.trackingId);
            draftState.voteState = OperationState.Executing;
            if (actionAny.__rest.operationKey) {
                operationSubmitting(draftState, actionAny.__rest.operationKey);
            }
            return;

        case Actions.VOTE_SUCCESS:
            const voteForTrackingId: string = (action as any).__rest.item.trackingId;
            markVotedAndShowSuccess(actionAny.__rest.item.id, voteForTrackingId, draftState, actionAny.__rest.operationKey,
                "Thank you for voting!");

            const voteTrackingId: string = (action as any).voteTrackingId || "";
            App.telemetry.postUserTask("Vote", TelemetryResult.Success, "", voteForTrackingId, {
                "VoteTrackingId": voteTrackingId,
                "TotalTimeMsec": (Date.now() - actionAny.voteStartTimeMsec).toString(),
            });
            draftState.voteState = OperationState.Success;
            return;

        case Actions.VOTE_FAILURE:
            App.telemetry.postUserTask("Vote", TelemetryResult.Failure, "", actionAny.__rest.item.trackingId);
            draftState.voteState = OperationState.Failed;

            // if already voted
            if (actionAny.__rest.error?.response?.status === HttpResponse.Conflict) {
                const voteForTrackingId: string = (action as any).__rest.item.trackingId;
                markVotedAndShowSuccess(actionAny.__rest.item.id, voteForTrackingId, draftState, actionAny.__rest.operationKey,
                    "Your have already voted for this feedback.");
            }
            else {
                App.telemetry.postCriticalFault("Vote", actionAny.errorMessage, null, actionAny.__rest.item.trackingId);
                if (actionAny.__rest.operationKey) {
                    operationFailed(draftState, actionAny.__rest.operationKey, ResourceStrings.failedVoting());
                }
            }
            return;

        // Need More Info
        case Actions.NMI_SET_COMMENT:
            draftState.nmiComment = (action as any).nmiComment;
            // tslint:disable-next-line:triple-equals
            draftState.nmiNextEnabled = draftState.nmiComment !== '';
            draftState.showRecordTip = containsRecordTipKeywords(draftState.nmiComment);
            return;

        case Actions.NMI_COMMENT_REQUEST:
            operationSubmitting(draftState, actionAny.operationKey);
            draftState.nmiState = OperationState.Executing;
            return;

        case Actions.NMI_COMMENT_SUCCESS:
            const addNmiCommentItem: PartialPost = (action as any).item;

            operationSuceeded(draftState, actionAny.operationKey, "Your comment was successfully submitted but it might take some time for the updates to show up. Please refresh this page after some time.");

            draftState.nmiState = OperationState.Success;
            // if on the same item in details view, update the comments to include the just added one
            //(it takes a bit to be processed on the backend - added to DevCom, indexed, etc.)
            if (draftState.selectedItem?.id === addNmiCommentItem.id) {
                const rootPost: RootPost = draftState.ticketsById[draftState.selectedItem?.id].data;
                addComment(rootPost, rootPost.currentUserId, draftState.userName, (action as any).data.comment);
            }
            draftState.nmiComment = "";
            draftState.selectedCommentsExpanded = true;

            // remove tracking Id from info needed and place in info provided list
            draftState.userProperties.markInfoProvided(addNmiCommentItem.trackingId);

            App.telemetry.postUserTask("AddNmiComment", TelemetryResult.Success, "", addNmiCommentItem.trackingId);
            return;

        case Actions.NMI_COMMENT_FAILURE:
            draftState.nmiState = OperationState.Failed;
            operationFailed(draftState, actionAny.operationKey, "There was an error in submitting your comment.");
            App.telemetry.postCriticalFault("NMIComment", actionAny.errorMessage, null, actionAny.item.trackingId);
            return;

        case Actions.TOGGLE_NMI_COMMENTS:
            draftState.nmiCommentsExpanded = !draftState.nmiCommentsExpanded;
            if (draftState.nmiCommentsExpanded) {
                App.telemetry.postUserTask("NMICommentsExpanded", TelemetryResult.Success);
            }
            return;

        case Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_REQUEST:
            draftState.routingMessageShouldShow = false;
            return;

        case Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_SUCCESS:
            draftState.routingMessageShouldShow = (action as any).req.data; //If the build is public, show the message, otherwise don't
            return;

        case Actions.RAP_DETERMINE_IS_PUBLIC_BUILD_FAILURE:
            draftState.routingMessageShouldShow = false;
            return;

        // search
        case Actions.SEARCH_UPDATE:
            draftState.problems.searchtext = (action as any).searchtext;
            draftState.suggestions.searchtext = (action as any).searchtext;
            return;

        case Actions.SEARCH_UPDATE_UI_CONTROL:
            draftState.problems.searchtext = (action as any).searchtext;
            draftState.suggestions.searchtext = (action as any).searchtext;
            draftState.event = EventName.setSearchBoxText;
            return;

        case Actions.UPDATE_COMMENT_TEXT:
            draftState.commentText = (action as any).commentText;
            draftState.detailAddCommentState = OperationState.NotStarted;
            return;

        case Actions.SET_SHOW_CLOSE_WARNING:
            draftState.showCloseWarning = (action as any).shouldShowCloseWarning;
            return;

        case Actions.SET_SHOW_DIALOG_SYSLOGS:
            draftState.showDialogSysLogs = (action as any).shouldShow;
            return;

        case Actions.SET_DIALOG:
            const dialog: Dialog = (action as any).dialog;
            const prevDialog = draftState.dialog
            draftState.dialog = dialog;
            // tslint:disable-next-line:triple-equals
            if ((draftState.dialog !== Dialog.none) && (draftState.dialog !== prevDialog)) // if just opened a dialog
            {
                const searchText: string = App.GetActiveSearchInfo(draftState).searchtext;
                if (searchText != null && searchText !== "") { // if searched for something
                    draftState.reportTitle = searchText; // show whatever we searched for as title for new problem
                }
                if (draftState.reportDetail == null || draftState.reportDetail === "") { // if nothing typed yet, or previous report submitted
                    draftState.reportDetail = App.settings.initialReproText || App.settings.reproTextTemplate || "";
                }
                if (draftState.dialog !== Dialog.add) { // if not the "Report Problem" dialog
                    // make sure we don't show repro steps template by accident
                    // @TODO: Remove this as it is no longer applicable
                    if (draftState.reportDetail === App.settings.reproTextTemplate) { // if repro template is showing
                        // remove repro steps since we're in the wrong dialog (not Report Problem)
                        draftState.reportDetail = "";
                    }

                    // since we're not in Report Problem, avoid accidental setting for "regression" and "worked in"
                    // Fixes Bug 945264: [VSF] If I previously check the optional item in "Report a Problem" page, "[regression] [worked-in:]" will appears in "Suggest a Feature" page.
                    draftState.reportIsRegression = false;
                    draftState.reportWorkedInText = "";
                    draftState.reportSeverityText = "";
                }
                App.settings.initialReproText = ""; // clear to only set it the first time we open Add Problem
                draftState.reportIncludeScreenshot = false;
                resetScreenRecordingState(draftState);
                draftState.reportIncludeSystemLogs = (draftState.dialog !== Dialog.suggest); // if not "suggestion" dialog, include system logs by default
                draftState.reportFilesToUpload = [];
                draftState.reportSystemFilesToUpload = draftState.reportSystemFilesToUpload ?? [];
                draftState.reportRunningVsInstances = [];
                draftState.reportIsCropping = false;
                draftState.reportCropRegion = null;
                draftState.hasRecorded = false;
                draftState.showRecordTip = false;
                draftState.voteState = OperationState.NotStarted;

                draftState.evComment = "";
                draftState.evNextEnabled = false;

                draftState.nmiComment = "";
                draftState.nmiNextEnabled = true;
                draftState.nmiState = OperationState.NotStarted;
                draftState.nmiCommentsExpanded = false;

                draftState.event = EventName.rapOpen;
                draftState.reportNextEnabled = shouldNextBeEnabled(draftState);
            }
            return;

        case Actions.SET_MAIN_VIEW:
            draftState.previousView = draftState.view;
            draftState.view = (action as any).view;
            draftState.commentText = "";
            return;

        // -------- Report problem dialog
        case Actions.RAP_SET_VIEW:
            const rapView: RapView = (action as any).view;
            // tslint:disable-next-line:triple-equals
            draftState.nmiNextEnabled = (rapView === RapView.describe && draftState.nmiComment === "") ? false : true;
            if (rapView === RapView.screenshot) {
                if (draftState.hasRecorded) {
                    draftState.showRecordTip = false;
                }
                else {
                    draftState.showRecordTip = containsRecordTipKeywords(draftState.reportTitle) || containsRecordTipKeywords(draftState.reportDetail)
                        || containsRecordTipKeywords(draftState.evComment) || containsRecordTipKeywords(draftState.nmiComment);
                }
            } else if (rapView === RapView.recprogress) {
                draftState.hasRecorded = true; // will be cleared in SET_MAIN_VIEW above
                draftState.showRecordTip = false;
            }

            draftState.rapView = rapView as any;

            if (draftState.rapView === RapView.processing) {
                App.read(ResourceStrings.processing());
                focusElement("addproblem");
            }
            return;

        case Actions.RAP_SET_TITLE:
            draftState.reportTitle = (action as any).title;

            draftState.showRecordTip = containsRecordTipKeywords(draftState.reportTitle);
            draftState.reportNextEnabled = shouldNextBeEnabled(draftState);
            rapSetDirty(draftState, "title", !!draftState.reportTitle);
            return;

        case Actions.RAP_SET_DETAIL:
            draftState.reportDetail = (action as any).detail;

            draftState.reportNextEnabled = shouldNextBeEnabled(draftState);
            draftState.showRecordTip = containsRecordTipKeywords(draftState.reportDetail);
            const isDirty: boolean = (draftState.reportDetail != null) &&
                (draftState.reportDetail !== "") &&
                (draftState.reportDetail !== beforeInstallTemplate);

            rapSetDirty(draftState, "description", isDirty);
            return;

        case Actions.RAP_SET_WORKED_IN_TEXT:
            draftState.reportWorkedInText = (action as any).text;
            rapSetDirty(draftState, "regressedIn", !!draftState.reportWorkedInText);
            return;

        case Actions.RAP_SET_CATEGORY:
            draftState.reportCategory = (action as any).key;
            return;

        case Actions.RAP_SET_SEVERITY_TEXT:
            draftState.reportSeverityText = (action as any).text;
            return;

        case Actions.RAP_UPDATE_INCLUDE_SCREENSHOT:
            draftState.reportIncludeScreenshot = actionAny.shouldInclude;
            rapSetDirty(draftState, "screenshot", draftState.reportIncludeScreenshot);
            App.telemetry.postUserTask("IncludeScreenshotClick", TelemetryResult.Success, undefined, undefined, {
                "NewValue": draftState.reportIncludeScreenshot.toString(),
            });
            return;

        case Actions.RAP_INCLUDE_SCREEN_RECORDING:
            draftState.reportIncludeScreenRecording = actionAny.shouldInclude;
            draftState.screenRecording = actionAny.video;
            rapSetDirty(draftState, "screenRecording", draftState.reportIncludeScreenRecording);
            App.telemetry.postUserTask("IncludeScreenRecordingClick", TelemetryResult.Success, undefined, undefined, {
                "NewValue": draftState.reportIncludeScreenRecording.toString(),
            });
            return;

        case Actions.SET_SCREEN_RECORDING_UPLOAD_STATE:
            draftState.screenRecordingUploadState = actionAny.state;
            draftState.screenRecordingErrorMessage = actionAny.error;
            draftState.screenRecordingPercentDone = actionAny.progressPercent;
            return;

        case Actions.RAP_UPDATE_INCLUDE_SYSTEM_LOGS:
            draftState.reportIncludeSystemLogs = (action as any).shouldInclude;
            App.telemetry.postUserTask("UpdateSystemLogsClick", TelemetryResult.Success, undefined, undefined, {
                "NewValue": draftState.reportIncludeSystemLogs.toString(),
            });
            return;

        case Actions.RAP_UPDATE_IS_REGRESSION:
            draftState.reportIsRegression = (action as any).shouldSetAsRegression;
            return;

        case Actions.RAP_ADD_FILES_TO_UPLOAD:
            draftState.reportFilesToUpload = draftState.reportFilesToUpload.concat((action as any).files);
            rapSetDirty(draftState, "attachments", draftState.reportFilesToUpload.length !== 0);
            return;

        case Actions.RAP_REMOVE_FILE_TO_UPLOAD:
            const file: FileInfo = (action as any).file;
            for (let i = 0; i < draftState.reportFilesToUpload.length; i++) {
                // eslint-disable-next-line security/detect-object-injection
                if (draftState.reportFilesToUpload[i].path === file.path) {
                    draftState.reportFilesToUpload.splice(i, 1);
                    break;
                }
            }
            rapSetDirty(draftState, "attachments", draftState.reportFilesToUpload.length !== 0);
            return;

        // system logs
        case Actions.RAP_GET_SYSTEM_FILES_REQUEST:
            draftState.reportGetSystemFilesState = OperationState.Executing;
            draftState.reportSystemFilesToUpload = [];
            return;

        case Actions.RAP_GET_SYSTEM_FILES_SUCCESS:
            if (!draftState.reportSystemFilesToUpload ||
                (draftState.reportSystemFilesToUpload.length === 0)) {
                let sysLogs = (action as any).files as FileInfo[];
                if (sysLogs && sysLogs.length !== 0) {
                    sysLogs = sysLogs.sort((a, b) => {
                        const isAInsignificant: number = insignificantSysLogPatterns.some(x => a.name.match(x)) ? 1 : 0;
                        const isBInsignificant: number = insignificantSysLogPatterns.some(x => b.name.match(x)) ? 1 : 0;
                        return isAInsignificant - isBInsignificant;
                    });
                }
                draftState.reportSystemFilesToUpload = sysLogs;
            }
            draftState.reportGetSystemFilesState = OperationState.Success;
            return;

        case Actions.RAP_GET_SYSTEM_FILES_FAILURE:
            draftState.reportSystemFilesToUpload = [];
            draftState.reportGetSystemFilesState = OperationState.Failed;
            return;

        // crop
        case Actions.RAP_SET_ISCROPPING:
            draftState.reportIsCropping = (action as any).value;
            return;

        case Actions.RAP_SET_IS_CROP_PROMPT_VISIBLE:
            draftState.reportIsCropPromptVisible = (action as any).value;
            return;

        case Actions.RAP_SET_CROP_REGION:
            draftState.reportCropRegion = (action as any).value;

            // compute cropped thumbnail, if any
            if (draftState.reportCropRegion == null) {
                draftState.reportCroppedScreenshotDataUrl = null; // no cropping
            } else {
                draftState.reportCroppedScreenshotDataUrl = (action as any).croppedImageDataUrl;
            }
            return;

        // enhance your vote
        case Actions.EV_SET_COMMENT:
            draftState.evComment = (action as any).evComment;
            // tslint:disable-next-line:triple-equals
            draftState.evNextEnabled = (draftState.evComment !== '');
            draftState.showRecordTip = containsRecordTipKeywords(draftState.evComment);
            return;

        // sign in
        case Actions.SET_SIGN_IN_STATE:
            draftState.signInState = actionAny.value;
            draftState.signInErrorMessage = actionAny.errorMessage;
            draftState.userName = actionAny.userName;
            draftState.userEmail = actionAny.userEmail;
            draftState.userVsid = actionAny.userVsid;
            draftState.userAccountType = actionAny.userAccountType;
            draftState.userLogo = actionAny.userLogo;

            const isAuthenticated = draftState.signInState === SignInState.Success;
            draftState.userRoles = [
                isAuthenticated && UserRole.AuthenticatedUsers,
                isAuthenticated && App.isMicrosoftAccount(draftState.userEmail) && UserRole.MicrosoftEmployees,
            ].filter(x => x);

            focusSignInNoteIfNeeded(draftState);
            App.telemetry.postOperation("SignInStateChange", TelemetryResult.Success, "", {
                NewSignInState: SignInState[draftState.signInState] ?? draftState.signInState,
                SignInErrorMessage: draftState.signInErrorMessage,
            });
            return;

        case Actions.SET_SIGN_IN_SUCCESS_ACTION:
            draftState.signInSuccessAction = actionAny.action;
            return;

        case Actions.SET_ACCOUNTS:
            draftState.accounts = actionAny.accounts;
            return;

        // -- routing rules
        case requestAction(Actions.GET_ROUTING_RULES):
            draftState.routingRules = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_ROUTING_RULES):
            draftState.routingRules = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_ROUTING_RULES):
            draftState.routingRules = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- mod and admin users
        case requestAction(Actions.GET_MOD_ADMIN_USERS):
            draftState.modAdminUsers = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_MOD_ADMIN_USERS):
            draftState.modAdminUsers = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_MOD_ADMIN_USERS):
            draftState.modAdminUsers = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- audit logs
        case requestAction(Actions.GET_AUDIT_LOGS):
            draftState.auditLogs = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_AUDIT_LOGS):
            draftState.auditLogs = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_AUDIT_LOGS):
            draftState.auditLogs = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- feature flags
        case requestAction(Actions.GET_FEATURE_FLAGS):
            draftState.featureFlags = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_FEATURE_FLAGS):
            draftState.featureFlags = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_FEATURE_FLAGS):
            draftState.featureFlags = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- admin site and space config
        case requestAction(Actions.GET_SITE_SPACE_CONFIG):
            draftState.serverConfig = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_SITE_SPACE_CONFIG):
            draftState.serverConfig = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_SITE_SPACE_CONFIG):
            draftState.serverConfig = Fetchable_Error(actionAny.errorMessage);
            return;

        // space config
        case requestAction(Actions.UPDATE_SPACE_CONFIG):
            draftState.updateSpaceConfig = Fetchable_InProgress();
            return;

        case successAction(Actions.UPDATE_SPACE_CONFIG):
            draftState.updateSpaceConfig = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.UPDATE_SPACE_CONFIG):
            draftState.updateSpaceConfig = Fetchable_Error(actionAny.errorMessage);
            return;

        // website config
        case requestAction(Actions.UPDATE_WEBSITE_CONFIG):
            draftState.updateWebsiteConfig = Fetchable_InProgress();
            return;

        case successAction(Actions.UPDATE_WEBSITE_CONFIG):
            draftState.updateWebsiteConfig = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.UPDATE_WEBSITE_CONFIG):
            draftState.updateWebsiteConfig = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- pipeline status
        case requestAction(Actions.GET_PIPELINE_STATUS):
            draftState.pipelineStatus = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_PIPELINE_STATUS):
            draftState.pipelineStatus = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_PIPELINE_STATUS):
            draftState.pipelineStatus = Fetchable_Error(actionAny.errorMessage);
            return;

        // -- gdpr status
        case requestAction(Actions.GET_GDPR_STATUS):
            draftState.gdprStatus = Fetchable_InProgress();
            return;

        case successAction(Actions.GET_GDPR_STATUS):
            draftState.gdprStatus = Fetchable_Success(actionAny.req.data);
            return;

        case failureAction(Actions.GET_GDPR_STATUS):
            draftState.gdprStatus = Fetchable_Error(actionAny.errorMessage);
            return;

        // -------- Is Moderator
        // This is only used to determine visibility of certain parts of UI for better user experience
        // Actual validation will still be done by backend APIs
        case requestAction(Actions.GET_USER_METADATA):
            draftState.userMetadataLoadState = OperationState.Executing;
            return;

        case successAction(Actions.GET_USER_METADATA):
            draftState.userMetadataLoadState = OperationState.Success;
            const inGroups: string[] = actionAny.req.data?.InGroups;
            const isModerator: boolean = inGroups?.includes(UserRole.Moderators) === true;
            const isAdmin: boolean = inGroups?.includes(UserRole.Admins) === true;

            // add missing user roles
            for (const group of inGroups) {
                if (draftState.userRoles.every(x => x !== group)) {
                    draftState.userRoles = [...draftState.userRoles, group as UserRole];
                }
            }

            // explicitly filter out moderator and admin since those are highly privileged, to avoid showing 
            // temporary UI that's not supported by backend when switching permissions in DB
            // note: this scenario is only valid when switching DB permissions with a browser front-end open
            // when page is refreshed, the UI with updated roles will be loaded from backend
            if (!isModerator) {
                draftState.userRoles = draftState.userRoles.filter(x => x !== UserRole.Moderators);
            }

            if (!isAdmin) {
                draftState.userRoles = draftState.userRoles.filter(x => x !== UserRole.Admins);
            }

            loadMeControl(draftState.userName, draftState.userEmail, actionAny.req.data?.Id, isAdmin || isModerator, draftState.userAccountType);

            const isInTestGroup: boolean = abTest(actionAny.req.data?.Id, isAdmin || isModerator);

            draftState.isUserBanned = actionAny.req.data?.IsBanned === true;
            draftState.useCategories = (actionAny.req.data?.UseCategories === true) && isInTestGroup;
            draftState.aiSummary = actionAny.req.data?.AISummary === true;
            return;

        case failureAction(Actions.GET_USER_METADATA):
            draftState.userMetadataLoadState = OperationState.Failed;
            // Treat user as not a moderator and not admin if we can't determine whether user is moderator or not
            draftState.userRoles = draftState.userRoles.filter(x => x !== UserRole.Moderators);
            draftState.userRoles = draftState.userRoles.filter(x => x !== UserRole.Admins);

            console.error("GetUserMetadata call failed");
            App.telemetry.postFault("GetUserMetadata", actionAny.errorMessage);
            return;

        case Actions.SET_USED_FORMATTING:
            draftState.usedFormatting = (action as any).value;
            return;

        // For setting the PID for which we collect diagnostics information
        case Actions.RAP_SET_RECORD_PID:
            draftState.reportRecordPid = (action as any).value;
            draftState.reportRecordbuttonEnabled = true;
            return;

        case Actions.SET_SETTINGS_UPDATE:
            draftState.settings_update = (action as any).value;
            return;

        // For setting that we are collecting diagnostics information
        case Actions.RAP_SET_RECORD_IN_PROGRESS:
            draftState.reportRecordInprogress = (action as any).value;
            return;

        case Actions.RAP_SET_POST_TYPE:
            draftState.rapPostType = (action as any).postType;
            return;

        case Actions.RAP_SET_SPACE_ID:
            draftState.reportCategory = "";
            draftState.reportSpaceId = (action as any).spaceId;
            return;

        case Actions.RAP_SET_DIAGNOCTIC_SESSION_ID:
            draftState.diagSessionId = (action as any).diagSessionId;
            return;

        case Actions.RAP_SET_ACTION_RECORDER_STATE:
            if ((action as any).data?.diagSessionId === draftState.diagSessionId) {
                draftState.actionRecorderState = (action as any).state;
                if (draftState.actionRecorderState === ActionRecorderState.started) {
                    draftState.actionRecorderSafeToCloseVS = false;
                }
                if (draftState.actionRecorderState !== ActionRecorderState.processing) {
                    draftState.filesBeingProcessed = [];
                }
                if (draftState.actionRecorderState === ActionRecorderState.processing) {
                    draftState.filesBeingProcessed = (action as any).data.files;
                }
                if (draftState.actionRecorderState === ActionRecorderState.failed) {
                    draftState.actionRecorderErrorMessage = (action as any).data?.errorMessage || "ERR_REC_FAILED";
                }
            }
            return;

        case Actions.RAP_SET_ACTION_RECORDER_SAFE_TO_CLOSE_VS:
            if ((action as any).data?.diagSessionId === draftState.diagSessionId) {
                draftState.actionRecorderSafeToCloseVS = true;
            }
            return;

        case Actions.RAP_SET_RUNNING_VS_INSTANCES:
            const instances: VSInstanceInfo[] = (action as any).processes;
            draftState.reportRunningVsInstances = instances;
            if (instances.length === 0) {
                draftState.reportRecordbuttonEnabled = false;
            }
            else {
                draftState.reportRecordbuttonEnabled = true;
            }
            return;

        case Actions.RAP_SET_PARENT_VS_INSTANCE_STATE:
            draftState.is_parent_vs_instance_running = (action as any).isParentVSInstanceRunning;
            return;

        case Actions.RAP_CLEAR_FORM:
            // @TODO: Check if anything else needs to be cleaned up
            draftState.reportState = OperationState.NotStarted;
            draftState.reportResultTicketUrl = null;
            draftState.detailAddCommentState = OperationState.NotStarted;
            draftState.commentText = "";
            draftState.evComment = "";
            draftState.reportTitle = "";
            draftState.reportDetail = "";
            draftState.reportErrorMessage = "";
            draftState.reportIncludeScreenshot = false;
            resetScreenRecordingState(draftState);
            draftState.reportIsRegression = false;
            draftState.reportWorkedInText = "";
            draftState.reportSeverityText = "";
            draftState.rapReportWhatsDirty = [];
            draftState.problems.searchtext = "";
            draftState.suggestions.searchtext = "";
            draftState.reportCategory = "";
            return;

        case Actions.RAP_SUBMIT_REQUEST:
            draftState.reportState = OperationState.Executing;
            return;

        case Actions.RAP_SUBMIT_SUCCESS:
            const feedback: PublicFeedback = (action as any).__rest.data as PublicFeedback;
            const feedbackId: string = (action as any).req.data.trackingId;
            draftState.reportResultTicketUrl = App.getTicketPath(actionAny.req.data.postId, draftState.reportTitle);
            draftState.rapReportWhatsDirty = [];

            const submissionStartTimeMsec: number = actionAny.__rest.data.submissionStartTimeMsec;
            const timeToSubmit = submissionStartTimeMsec ? Date.now() - submissionStartTimeMsec : null;

            const usedFormattingGuess: boolean = Minimark.GuessHasMarkdownFormatting(draftState.reportDetail);

            // if no tracking id, report failure
            if ((feedbackId == null) || (feedbackId === "")) { // extra logging for no tracking ID
                App.telemetry.postUserTask("SubmitNewProblem", TelemetryResult.Failure);
                draftState.reportState = OperationState.Failed;

                let data2 = (action as any).req.data;
                data2 = data2 || {};
                App.telemetry.postCriticalFault("SubmitNewProblemNoTrackingId", "No trackingId returned by server", undefined, null, {
                    feedback: JSON.stringify(feedback),
                    data: JSON.stringify(data2),
                    usedFormatting: (usedFormattingGuess || draftState.usedFormatting).toString(),
                    usedFormattingGuess: usedFormattingGuess.toString(),
                    usedFormattingCertain: draftState.usedFormatting.toString(),
                    spaceId: actionAny.__rest.data.spaceId,
                    postType: actionAny.__rest.data.type,
                    timeToSubmit: timeToSubmit?.toString(),
                });
                return;
            }

            // since we have a tracking ID, we succeeded
            App.telemetry.postUserTask("SubmitNewProblem", TelemetryResult.Success, "", feedbackId, {
                usedFormatting: (usedFormattingGuess || draftState.usedFormatting).toString(),
                usedFormattingGuess: usedFormattingGuess.toString(),
                usedFormattingCertain: draftState.usedFormatting.toString(),
                spaceId: actionAny.__rest.data.spaceId,
                postType: actionAny.__rest.data.type,
                timeToSubmit: timeToSubmit?.toString(),
            });
            draftState.reportState = OperationState.Success;
            draftState.submittedPostRecently = true;

            // since we succeeded, clear report text for next time this dialog is opened and clean up formatting use flag
            draftState.reportIsRegression = false;
            draftState.reportWorkedInText = "";
            draftState.reportSeverityText = "";
            draftState.reportTitle = "";
            draftState.reportDetail = "";
            draftState.usedFormatting = false;
            draftState.reportFilesToUpload = [];
            draftState.reportIncludeScreenshot = false;
            draftState.rapReportWhatsDirty = [];
            draftState.actionRecorderState = ActionRecorderState.notStarted;
            draftState.actionRecorderSafeToCloseVS = false;
            draftState.diagSessionId++;    // Prevent older action recording session from making any UI updates after report has been submitted
            return;

        case Actions.RAP_SUBMIT_FAILURE:
            const error = (action as any).error;
            draftState.reportErrorMessage = stringFromErrorOrAny(error);
            draftState.reportState = OperationState.Failed;
            draftState.rapReportWhatsDirty = [];
            App.telemetry.postUserTask("SubmitNewProblem", TelemetryResult.Failure, draftState.reportErrorMessage);
            App.telemetry.postCriticalFault("SubmitNewProblem", draftState.reportErrorMessage);
            return;

        case Actions.RAP_CLEAR_SUBMITTED_POST_RECENTLY:
            draftState.submittedPostRecently = false;
            return;

        // if  MS internal, used to not show results or start searching until signed in
        case Actions.SET_SEARCH_NOT_STARTED:
            const infoNotStarted: SearchInfo = getSearchInfoFromState(draftState, (action as any).target);
            infoNotStarted.state = OperationState.NotStarted;
            setSearchInfo(draftState, infoNotStarted, (action as any).target);
            return;

        case Actions.SEARCH_REQUEST: {
            const infoRequest: SearchInfo = getSearchInfoFromState(draftState, (action as any).target);
            const userQuery: TagsResult = (action as any).userQuery;

            infoRequest.lastSearchId = (action as any).currSearchId; // save the last search request
            infoRequest.state = OperationState.Executing;
            infoRequest.searchtext = userQuery.text;
            infoRequest.searchTags = determineTags(userQuery, []);
            infoRequest.lastSearchTimeMsec = new Date().getTime();
            infoRequest.isLoadingMore = (action as any).skip;
            infoRequest.loadedEverything = true;
            infoRequest.userName = userQuery.userName;
            if (infoRequest.userName) {
                App.telemetry.postUserTask("UserFilter", TelemetryResult.Success, undefined, undefined, {
                    "UserName": infoRequest.userName
                });
            }

            setSearchInfo(draftState, infoRequest, (action as any).target);
            // @TODO: Reducers ideally shouldn't do any DOM mutations. They should be pure functions
            // Probably move the text to be read to store and read from there or move this code out of reducer
            App.read(ResourceStrings.loading());
            return;
        }

        case Actions.SEARCH_SUCCESS: {
            const infoSuccess: SearchInfo = getSearchInfoFromState(draftState, (action as any).target);
            if ((action as any).currSearchId !== infoSuccess.lastSearchId) { // ignore old search requests
                return;
            }

            const skipCount: number = (action as any).skip;
            infoSuccess.isLastSearchAdditive = skipCount > 0;
            infoSuccess.loadedEverything = true;

            const data: SearchResults = (action as any).req.data;
            if ((data.results != null) && Array.isArray(data.results) && (data.results.length > 0)) {
                data.results.forEach((dataItem: SearchResult) => {
                    dataItem.id = dataItem.answerHubId;
                    dataItem.text = App.HtmlFromMarkdownIfNeeded(dataItem.text);
                    // get hasVoted from client cache
                    const hasVotedCached: boolean = App.hasVotedCache[dataItem.trackingId];
                    if (hasVotedCached != null) {
                        if (!dataItem.hasVoted && hasVotedCached) {
                            dataItem.totalVotes++;
                        }
                        dataItem.hasVoted = hasVotedCached;
                    }
                });
                infoSuccess.loadedEverything = false;
            }

            if (!draftState.hasSearched) {
                draftState.hasSearched = true;
                App.checkForSignIn(); // first check for signed in after init
            }
            const userQuery2: TagsResult = (action as any).userQuery;

            infoSuccess.state = OperationState.Success;
            if (!actionAny.notAdditive && skipCount > 0) { // if additive search
                // add extra results to bottom
                infoSuccess.results.results = infoSuccess.results.results.concat(data.results || []);
            }
            else {
                infoSuccess.results = data;
            }
            infoSuccess.searchTags = determineTags(userQuery2, []);

            let totalCount: number;
            if (data.countsByType) {
                const homePageSearchTargets: SearchInfoTarget[] = [SearchInfoTarget.myActivity, SearchInfoTarget.myProblems, SearchInfoTarget.mySuggestions];
                if (homePageSearchTargets.indexOf((action as any).target) >= 0) { // if on home page
                    draftState.countsByTypeHome = data.countsByType;
                } else {
                    draftState.countsByTypeSearchPage = data.countsByType;
                }

                totalCount = data.countsByType.problem + data.countsByType.idea;
            }

            setSearchInfo(draftState, infoSuccess, (action as any).target);
            App.telemetry.postUserTask("Search", TelemetryResult.Success, undefined, undefined, {
                "ResultCount": data.results.length.toString(),
                "SearchText": infoSuccess.searchtext,
                "TotalTimeMsec": ((new Date().getTime()) - actionAny.data.searchStartTimeMsec).toString(),
                "Sort": draftState.sort,
                "Filter": draftState.filter,
                "SearchSource": actionAny.data.searchSource,
            });
            App.read(ResourceStrings.loadingSuccess() + (totalCount == null || totalCount === undefined ? '' : ` Found ${totalCount} matches.`));
            return;
        }

        case Actions.SEARCH_FAILURE: {
            const infoFailure: SearchInfo = getSearchInfoFromState(draftState, (action as any).target);
            if ((action as any).currSearchId !== infoFailure.lastSearchId) { // ignore old search requests
                return;
            }

            infoFailure.state = OperationState.Failed;

            // get error message, if any
            const searchError = (action as any).errorMessage;
            infoFailure.errorMessage = searchError;
            console.log(infoFailure.errorMessage);
            // if it's the first search, check for sign in after. This is perf optimization due to startup cost for sign in
            if (!draftState.hasSearched) {
                draftState.hasSearched = true;
                App.checkForSignIn(); // first check for signed in after init
            }

            App.telemetry.postUserTask("Search", TelemetryResult.Failure, undefined, undefined, {
                "ErrorMessage": infoFailure.errorMessage.toString(),
                "SearchText": infoFailure.searchtext,
                "TotalTimeMsec": ((new Date().getTime()) - actionAny.data.searchStartTimeMsec).toString(),
                "SearchSource": actionAny.data.searchSource,
            });
            App.telemetry.postCriticalFault("Search", searchError);
            return;
        }

        case Actions.VECTOR_SEARCH_REQUEST: {
            const infoRequest: VectorSearchInfo = draftState.vectorSuggestions;

            infoRequest.lastSearchId = actionAny.currSearchId; // save the last search request
            infoRequest.lastSearchTimeMsec = new Date().getTime();
            infoRequest.state = OperationState.Executing;
            infoRequest.loadedEverything = true;
            infoRequest.searchTitle = actionAny.title;

            draftState.vectorSuggestions = infoRequest;

            // @TODO: Reducers ideally shouldn't do any DOM mutations. They should be pure functions
            // Probably move the text to be read to store and read from there or move this code out of reducer
            App.read(ResourceStrings.loading());
            return;
        }

        case Actions.VECTOR_SEARCH_SUCCESS: {
            const infoSuccess: VectorSearchInfo = draftState.vectorSuggestions;
            if (actionAny.currSearchId !== infoSuccess.lastSearchId) { // ignore old search requests
                return;
            }

            const data: SearchResults = actionAny.req.data;
            if ((data.results != null) && Array.isArray(data.results) && (data.results.length > 0)) {
                data.results.forEach((dataItem: SearchResult) => {
                    dataItem.id = dataItem.answerHubId;
                    dataItem.text = App.HtmlFromMarkdownIfNeeded(dataItem.text);
                    // get hasVoted from client cache
                    const hasVotedCached: boolean = App.hasVotedCache[dataItem.trackingId];
                    if (hasVotedCached != null) {
                        if (!dataItem.hasVoted && hasVotedCached) {
                            dataItem.totalVotes++;
                        }
                        dataItem.hasVoted = hasVotedCached;
                    }
                });
                infoSuccess.loadedEverything = false;
            }

            infoSuccess.state = OperationState.Success;
            infoSuccess.results = data;

            draftState.vectorSuggestions = infoSuccess;

            const totalCount: number = data.results.length;

            App.telemetry.postUserTask("VectorSearch", TelemetryResult.Success, undefined, undefined, {
                "ResultCount": totalCount.toString(),
                "SearchTitle": infoSuccess.searchTitle,
                "TotalTimeMsec": ((new Date().getTime()) - actionAny.data.searchStartTimeMsec).toString(),
                "SearchSource": actionAny.data.searchSource,
            });
            App.read(ResourceStrings.loadingSuccess() + (totalCount == null || totalCount === undefined ? '' : ` Found ${totalCount} matches.`));
            return;
        }

        case Actions.VECTOR_SEARCH_FAILURE: {
            const infoFailure: VectorSearchInfo = draftState.vectorSuggestions;
            if (actionAny.currSearchId !== infoFailure.lastSearchId) { // ignore old search requests
                return;
            }

            // get error message, if any
            const searchError = actionAny.errorMessage;

            infoFailure.state = OperationState.Failed;
            infoFailure.errorMessage = searchError;
            console.log(infoFailure.errorMessage);

            App.telemetry.postUserTask("VectorSearch", TelemetryResult.Failure, undefined, undefined, {
                "ErrorMessage": infoFailure.errorMessage.toString(),
                "SearchTitle": infoFailure.searchTitle,
                "TotalTimeMsec": ((new Date().getTime()) - actionAny.data.searchStartTimeMsec).toString(),
                "SearchSource": actionAny.data.searchSource,
            });
            App.telemetry.postCriticalFault("VectorSearch", searchError);
            return;
        }

        case Actions.LOAD_ACTIVITY_REQUEST:
            draftState.myActivity.lastSearchId = (action as any).currGetActivityId; // save the last search request
            draftState.myActivity.state = OperationState.Executing;
            draftState.myActivity.isLoadingMore = (action as any).pageNumberFromOne > 1;
            draftState.myActivity.lastSearchTimeMsec = new Date().getTime();
            draftState.myActivity.loadedEverything = true;
            return;

        case Actions.LOAD_ACTIVITY_SUCCESS:
            if ((action as any).currGetActivityId !== draftState.myActivity.lastSearchId) { // ignore old search requests
                return;
            }

            const pageNumberFromOne: number = (action as any).pageNumberFromOne;
            const actResults: ActivityResults = (action as any).req.data;
            actResults.activities = actResults.activities || [];

            const hasActivities: boolean = (actResults.activities != null) && Array.isArray(actResults.activities) && (actResults.activities.length > 0);
            draftState.myActivity.loadedEverything = !hasActivities; // if we have no more activities, everything is loaded
            draftState.myActivity.isLastSearchAdditive = pageNumberFromOne > 1;

            if (draftState.myActivity.isLastSearchAdditive) {
                // add extra results to bottom
                draftState.myActivity.activityResults.activities = draftState.myActivity.activityResults.activities.concat(actResults.activities || []);
            }
            else {
                draftState.myActivity.activityResults = actResults;
            }
            draftState.myActivity.state = OperationState.Success;

            App.telemetry.postUserTask("LoadActivity", TelemetryResult.Success, undefined, undefined, {
                "TotalTimeMsec": ((new Date().getTime()) - draftState.myActivity.lastSearchTimeMsec).toString(),
                "Sort": draftState.sort,
                "Filter": draftState.filter,
            });
            return;

        case Actions.LOAD_ACTIVITY_FAILURE:
            if ((action as any).currGetActivityId !== draftState.myActivity.lastSearchId) { // ignore old search requests
                return;
            }

            draftState.myActivity.state = OperationState.Failed;

            // get error message, if any
            const activityError = (action as any).error;
            draftState.myActivity.errorMessage = stringFromErrorOrAny(activityError);
            console.log(draftState.myActivity.errorMessage);
            // if it's the first search, check for sign in after. This is perf optimization due to startup cost for sign in
            if (!draftState.hasSearched) {
                draftState.hasSearched = true;
                App.checkForSignIn(); // first check for signed in after init
            }

            App.telemetry.postUserTask("LoadActivity", TelemetryResult.Failure, undefined, undefined, {
                "ErrorMessage": draftState.myActivity.errorMessage.toString(),
                "TotalTimeMsec": ((new Date().getTime()) - draftState.myActivity.lastSearchTimeMsec).toString(),
            });
            return;

        case Actions.SET_HAS_SEARCHED:
            draftState.hasSearched = true;
            return;

        case Actions.SET_ACTIVE_FOLLOW_TAB:
            draftState.followActiveTab = (action as any).tab;
            return;

        case Actions.SET_ACTIVE_SEARCH_TAB:
            draftState.searchActiveTab = (action as any).tab;
            App.telemetry.postBusinessEvent("search-tab-clicked", {
                "sort": draftState.sort,
                "searchActiveTab": draftState.searchActiveTab,
            });
            return;

        case successAction(Actions.GET_APPROVED_FILE_EXTENSIONS):
            draftState.approvedFileExtensionsCsv = actionAny.req?.data ? actionAny.req.data.replace(/\|/g, ', ') : '';
            return;

        // -------- LOAD_USER_PROPERTIES
        case Actions.LOAD_USER_PROPERTIES_REQUEST:
            return;

        case Actions.LOAD_USER_PROPERTIES_SUCCESS:
            const dataProps: UserProperties = (action as any).req.data || new UserProperties();
            const props: UserProperties = new UserProperties(); // we new () the props here to ensure all class functions are present

            props.infoNeeded = dataProps.infoNeeded || [];
            props.infoProvided = dataProps.infoProvided || [];
            props.needMoreInfoFromMe = {};

            props.infoNeeded.forEach((trackingId: string) => {
                // eslint-disable-next-line security/detect-object-injection
                props.needMoreInfoFromMe[trackingId] = true;
            });
            props.infoProvided.forEach((trackingId: string) => {
                // eslint-disable-next-line security/detect-object-injection
                props.needMoreInfoFromMe[trackingId] = false;
            });

            draftState.userProperties = props;
            return;

        case Actions.LOAD_USER_PROPERTIES_FAILURE:
            draftState.userProperties = new UserProperties();
            return;

        // ------------------- Profile and Preferences -------------------
        case Actions.VIEW_ATTACHMENTS_REQUEST:
            draftState.profileAttachmentsLoadState = OperationState.Executing;
            draftState.profileAttachments = null;
            draftState.profileAttachmentsErrorMessage = "";
            return;

        case Actions.VIEW_ATTACHMENTS_SUCCESS:
            draftState.profileAttachmentsLoadState = OperationState.Success;
            draftState.profileAttachments = (action as any).req.data;
            return;

        case Actions.VIEW_ATTACHMENTS_FAILURE:
            draftState.profileAttachmentsErrorMessage = (action as any).errorMessage;
            draftState.profileAttachmentsLoadState = OperationState.Failed;
            return;

        case requestAction(Actions.DELETE_ATTACHMENT):
            draftState.deleteAttachmentState = OperationState.Executing;
            draftState.deleteAttachmentErrorMessage = "";
            return;

        case successAction(Actions.DELETE_ATTACHMENT):
            draftState.deleteAttachmentState = OperationState.Success;
            // Remove the deleted attachment from the local state
            if (draftState.profileAttachments) {
                const deletedTransferId = (action as any).deletedTransferId;
                draftState.profileAttachments = draftState.profileAttachments.filter(
                    attachment => attachment.transferId !== deletedTransferId
                );
            }
            return;

        case failureAction(Actions.DELETE_ATTACHMENT):
            draftState.deleteAttachmentState = OperationState.Failed;
            draftState.deleteAttachmentErrorMessage = stringFromErrorOrAny(actionAny.errorMessage);
            return;

        case Actions.CREATE_ARCHIVE_REQUEST:
            draftState.archiveCreationState = OperationState.Executing;
            draftState.archiveCreationErrorMessage = "";
            return;

        case Actions.CREATE_ARCHIVE_SUCCESS:
            draftState.archiveCreationState = OperationState.Success;
            return;

        case Actions.CREATE_ARCHIVE_FAILURE:
            draftState.archiveCreationState = OperationState.Failed;
            draftState.archiveCreationErrorMessage = (action as any).errorMessage;
            return;

        case Actions.ARCHIVE_STATUS_REQUEST:
            draftState.archiveStatusLoadState = OperationState.Executing;
            draftState.archiveCurrentStatus = null;
            draftState.archiveStatusErrorMessage = "";
            return;

        case Actions.ARCHIVE_STATUS_SUCCESS:
            draftState.archiveStatusLoadState = OperationState.Success;
            draftState.archiveCurrentStatus = (action as any).req.data;
            // TODO: remove after testing
            console.log("Archive status");
            console.log(draftState.archiveCurrentStatus);
            return;

        case Actions.ARCHIVE_STATUS_FAILURE:
            draftState.archiveStatusLoadState = OperationState.Failed;
            draftState.archiveStatusErrorMessage = (action as any).errorMessage;
            return;

        case Actions.USER_SERVER_SETTINGS_REQUEST:
            draftState.userServerSettingsLoadState = OperationState.Executing;
            draftState.userServerSettings = null;
            draftState.userServerSettingsErrorMessage = "";
            return;

        case Actions.USER_SERVER_SETTINGS_SUCCESS:
            draftState.userServerSettingsLoadState = OperationState.Success;
            draftState.userServerSettings = (action as any).req.data;
            return;

        case Actions.USER_SERVER_SETTINGS_FAILURE:
            draftState.userServerSettingsLoadState = OperationState.Failed;
            draftState.userServerSettingsErrorMessage = (action as any).errorMessage;
            return;

        case Actions.SET_USER_SERVER_SETTINGS_LOCALLY:
            draftState.userServerSettings = (action as any).newSettings;
            console.log("draftState.userServerSettings");
            console.log(draftState.userServerSettings);
            return;

        case Actions.SET_USER_SERVER_SETTINGS_REQUEST:
            draftState.userServerSettingsSaveState = OperationState.Executing;
            draftState.userServerSettingsSaveErrorMessage = "";
            return;

        case Actions.SET_USER_SERVER_SETTINGS_SUCCESS:
            draftState.userServerSettingsSaveState = OperationState.Success;
            return;

        case Actions.SET_USER_SERVER_SETTINGS_FAILURE:
            draftState.userServerSettingsSaveState = OperationState.Failed;
            draftState.userServerSettingsSaveErrorMessage = (action as any).errorMessage;
            return;

        // TODO: how does the delete account state work?
        case Actions.DELETE_ACCOUNT_REQUEST:
            draftState.deleteAccountLoadState = OperationState.Executing;
            draftState.deleteAccountErrorMessage = "";
            return;

        case Actions.DELETE_ACCOUNT_SUCCESS:
            draftState.deleteAccountLoadState = OperationState.Success;
            return;

        case Actions.DELETE_ACCOUNT_FAILURE:
            draftState.deleteAccountLoadState = OperationState.Failed;
            draftState.deleteAccountErrorMessage = (action as any).errorMessage;
            return;

        case Actions.USER_PROFILE_LOAD_REQUEST:
            draftState.userProfileLoadState = OperationState.Executing;
            draftState.userProfile = null;
            draftState.userProfileErrorMessage = "";
            return;

        case Actions.USER_PROFILE_LOAD_SUCCESS:
            draftState.userProfileLoadState = OperationState.Success;
            const data = (action as any).req.data;
            draftState.userProfile = {
                userName: data.userName,
                userPhoto: data.userPhoto,
                vsid: data.vsid,
                email: data.email,
                isSuspended: data.isSuspended
            };
            return;

        case Actions.USER_PROFILE_LOAD_FAILURE:
            draftState.userProfileLoadState = OperationState.Failed;
            draftState.userProfileErrorMessage = (action as any).errorMessage;
            return;


        // ---------------- SUGGEST ------------------------------------------------
        case Actions.SUGGEST_REQUEST:
            if ((action as any).__rest.currSuggestId !== Actions.lastSuggestId) { // ignore old suggest requests
                return;
            }

            draftState.suggeststate = OperationState.Executing;
            draftState.lastSuggestTimeMsec = new Date().getTime();
            return;

        case Actions.SUGGEST_SUCCESS:
            if ((action as any).__rest.currSuggestId !== Actions.lastSuggestId) { // ignore old suggest requests
                return;
            }

            const suggestResults: SuggestQueryResults = (action as any).req.data;
            if (suggestResults.suggestions == null) {
                suggestResults.suggestions = [];
            }

            draftState.reportSuggestions = suggestResults.suggestions;
            draftState.event = EventName.suggestRecalc;

            draftState.suggeststate = OperationState.Success;
            App.telemetry.postUserTask("Suggest", TelemetryResult.Success, undefined, undefined, {
                "ResultCount": suggestResults.suggestions.length.toString(),
                "SearchText": App.GetActiveSearchInfo(draftState).searchtext,
                "TotalTimeMsec": ((new Date().getTime()) - draftState.lastSuggestTimeMsec).toString(),
            });
            return;

        case Actions.SUGGEST_FAILURE:
            if ((action as any).__rest.currSuggestId !== Actions.lastSuggestId) { // ignore old suggest requests
                return;
            }

            draftState.reportSuggestions = [];
            draftState.event = EventName.suggestRecalc;

            draftState.suggeststate = OperationState.Failed;
            App.telemetry.postUserTask("Suggest", TelemetryResult.Failure, undefined, undefined, {
                "SearchText": App.GetActiveSearchInfo(draftState).searchtext,
                "TotalTimeMsec": ((new Date().getTime()) - draftState.lastSuggestTimeMsec).toString(),
            });
            return;

        // -------- ANNOUNCEMENTS
        case Actions.LOAD_ANNOUNCEMENTS_REQUEST:
            draftState.announcementsLoadState = OperationState.Executing;
            draftState.announcementErrorMessage = "";
            return;

        case Actions.LOAD_ANNOUNCEMENTS_SUCCESS:
            draftState.announcements = (action as any).req.data || new AnnouncementInfo();
            // uncomment for debugging:
            // if (draftState.announcements.preview) {
            //     draftState.announcements.preview.fixedInTopics = [
            //         { issueCount: 34, shortName: "hello world", url: "https://microsoft.com" },
            //         { issueCount: 12, shortName: "preview 1", url: "https://google.com" },
            //     ];
            // }
            if (draftState.announcements.release == null) {
                draftState.selectedAnnouncement = "preview";
            }
            draftState.announcementsLoadState = OperationState.Success;
            return;

        case Actions.LOAD_ANNOUNCEMENTS_FAILURE:
            draftState.announcementsLoadState = OperationState.Failed;
            draftState.announcements = new AnnouncementInfo();
            draftState.announcementErrorMessage = (action as any).errorMessage;
            return;

        case Actions.SET_SELECTED_ANNOUNCEMENT:
            draftState.selectedAnnouncement = (action as any).selectedAnnouncement;
            return;

        // -------- INCIDENTS
        case Actions.LOAD_INCIDENTS_REQUEST:
            draftState.incidentsLoadState = OperationState.Executing;
            draftState.incidentErrorMessage = "";
            return;

        case Actions.LOAD_INCIDENTS_SUCCESS:
            draftState.incidents = (action as any).req.data || new IncidentInfo();
            draftState.incidentsLoadState = OperationState.Success;
            return;

        case Actions.LOAD_INCIDENTS_FAILURE:
            draftState.incidentsLoadState = OperationState.Failed;
            draftState.incidents = new IncidentInfo();
            draftState.incidentErrorMessage = (action as any).errorMessage;
            return;

        // -------- Outages
        case Actions.LOAD_VSSTATUS_OUTAGE_REQUEST:
            draftState.outageLoadState = OperationState.Executing;
            draftState.outageErrorMessage = "";
            return;

        case Actions.LOAD_VSSTATUS_OUTAGE_SUCCESS:
            draftState.outage = (action as any).req.data || [];
            draftState.outage = addHttpsToUrl(draftState.outage);
            draftState.outageLoadState = OperationState.Success;
            return;

        case Actions.LOAD_VSSTATUS_OUTAGE_FAILURE:
            draftState.outageLoadState = OperationState.Failed;
            draftState.outage = [];
            draftState.outageErrorMessage = (action as any).errorMessage;
            return;

        // -------- Custom Outages
        case Actions.LOAD_CUSTOM_OUTAGE_REQUEST:
            draftState.customOutageLoadState = OperationState.Executing;
            draftState.customOutageErrorMessage = "";
            return;

        case Actions.LOAD_CUSTOM_OUTAGE_SUCCESS:
            draftState.customOutage = (action as any).req.data || [];
            draftState.customOutage = addHttpsToUrl(draftState.customOutage);
            draftState.customOutageLoadState = OperationState.Success;
            return;

        case Actions.LOAD_CUSTOM_OUTAGE_FAILURE:
            draftState.customOutageLoadState = OperationState.Failed;
            draftState.customOutage = [];
            draftState.customOutageErrorMessage = (action as any).errorMessage;
            return;

        case Actions.RELEASE_ACTION_INFO_REQUEST:
            draftState.releaseActionLoadState = OperationState.Executing;
            draftState.releaseActionErrorMessage = "";
            return;

        case Actions.RELEASE_ACTION_INFO_SUCCESS:
            draftState.releaseActionLoadState = OperationState.Success;
            draftState.releaseActionInfo = (action as any).req.data;
            App.telemetry.postOperation("LoadReleaseAction", TelemetryResult.Success, "", {
                userId: draftState.userVsid,
                action: ReleaseAction[draftState.releaseActionInfo.action],
                version: draftState.releaseActionInfo.version,
            });
            return;

        case Actions.RELEASE_ACTION_INFO_FAILURE:
            draftState.releaseActionLoadState = OperationState.Failed;
            draftState.releaseActionErrorMessage = (action as any).errorMessage;
            App.telemetry.postOperation("LoadReleaseAction", TelemetryResult.Failure, "", {
                userId: draftState.userVsid,
                channelManifestId: App.channelManifestId,
                product: App.clientName,
            });
            return;

        // -------- Moderation
        // Get list of posts
        case requestAction(Actions.LOAD_FLAGGED_POSTS):
            draftState.moderationPosts = {
                isFetching: true,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: 0,
                    items: []
                }
            } as FetchablePagedList<ModerationPost>;
            return;

        case successAction(Actions.LOAD_FLAGGED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: actionAny.req.data.totalItemsCount,
                    items: actionAny.req.data.items ?? []
                },
            } as FetchablePagedList<ModerationPost>;

            draftState.moderationPosts.data.items.forEach((post: ModerationPost) => {
                post.rootPostTitle = App.HtmlFromMarkdownIfNeeded(post.rootPostTitle);
                post.content = App.HtmlFromMarkdownIfNeeded(post.content);
            });

            App.telemetry.postOperation("ModerationPostsLoaded", TelemetryResult.Success);
            return;

        case failureAction(Actions.LOAD_FLAGGED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: true,
            } as FetchablePagedList<ModerationPost>;

            App.telemetry.postOperation("ModerationPostsLoaded", TelemetryResult.Failure);
            return;

        // Get list of recently created posts by external users
        case requestAction(Actions.LOAD_RECENTLY_CREATED_POSTS):
            draftState.moderationPosts = {
                isFetching: true,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: 0,
                    items: []
                }
            } as FetchablePagedList<ModerationPost>;
            return;

        case successAction(Actions.LOAD_RECENTLY_CREATED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: actionAny.req.data.totalItemsCount,
                    items: actionAny.req.data.items ?? []
                },
            } as FetchablePagedList<ModerationPost>;

            draftState.moderationPosts.data.items.forEach((post: ModerationPost) => {
                post.rootPostTitle = App.HtmlFromMarkdownIfNeeded(post.rootPostTitle);
                post.content = App.HtmlFromMarkdownIfNeeded(post.content);
            });

            App.telemetry.postOperation("Moderation - Recently created posts by external users loaded", TelemetryResult.Success);
            return;

        case failureAction(Actions.LOAD_RECENTLY_CREATED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: true,
            } as FetchablePagedList<ModerationPost>;

            App.telemetry.postOperation("Moderation - Recently created posts by external users failed", TelemetryResult.Failure);
            return;

        // Get list of auto rejected posts
        case requestAction(Actions.LOAD_AUTO_REJECTED_POSTS):
            draftState.moderationPosts = {
                isFetching: true,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: 0,
                    items: []
                }
            } as FetchablePagedList<ModerationPost>;
            return;

        case successAction(Actions.LOAD_AUTO_REJECTED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: actionAny.req.data.totalItemsCount,
                    items: actionAny.req.data.items ?? []
                },
            } as FetchablePagedList<ModerationPost>;

            draftState.moderationPosts.data.items.forEach((post: ModerationPost) => {
                post.rootPostTitle = App.HtmlFromMarkdownIfNeeded(post.rootPostTitle);
                post.content = App.HtmlFromMarkdownIfNeeded(post.content);
            });

            App.telemetry.postOperation("Moderation - Auto rejected posts loaded successfully", TelemetryResult.Success);
            return;

        case failureAction(Actions.LOAD_AUTO_REJECTED_POSTS):
            draftState.moderationPosts = {
                isFetching: false,
                hasErrors: true,
            } as FetchablePagedList<ModerationPost>;

            App.telemetry.postOperation("Moderation - Auto rejected posts failed", TelemetryResult.Failure);
            return;

        // Publish post
        case requestAction(Actions.MODERATE_PUBLISH_POST):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.MODERATE_PUBLISH_POST):
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, "Post successfully published.");
            App.telemetry.postUserTask("ModerationPublishPost", TelemetryResult.Success);
            return;

        case failureAction(Actions.MODERATE_PUBLISH_POST):
            operationFailed(draftState, actionAny.__rest.data.operationKey, "There was an error in publishing post");
            App.telemetry.postUserTask("ModerationPublishPost", TelemetryResult.Failure);
            return;

        // Reject post
        case requestAction(Actions.MODERATE_REJECT_POST):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.MODERATE_REJECT_POST):
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, "Post successfully rejected.");
            App.telemetry.postUserTask("ModerationRejectPost", TelemetryResult.Success);
            return;

        case failureAction(Actions.MODERATE_REJECT_POST):
            operationFailed(draftState, actionAny.__rest.data.operationKey, "There was an error in rejecting post");
            App.telemetry.postUserTask("ModerationRejectPost", TelemetryResult.Failure);
            return;

        // User suspension
        case requestAction(Actions.UPDATE_USER_SUSPENSION_STATE):
            operationSubmitting(draftState, actionAny.__rest.data.operationKey);
            return;

        case successAction(Actions.UPDATE_USER_SUSPENSION_STATE):
            operationSuceeded(draftState, actionAny.__rest.data.operationKey, `User succesfully ${actionAny.__rest.data.actionType === SuspensionActionType.Suspend ? 'suspended' : 'unsuspended'}`);
            draftState.usersUpdatedSuspensionState[actionAny.__rest.data.userId] = actionAny.__rest.data.actionType === SuspensionActionType.Suspend;
            App.telemetry.postUserTask("UpdateUserSuspensionState", TelemetryResult.Success);
            return;

        case failureAction(Actions.UPDATE_USER_SUSPENSION_STATE):
            operationFailed(draftState, actionAny.__rest.data.operationKey, `There was an error in  ${actionAny.__rest.data.actionType === SuspensionActionType.Suspend ? 'suspending' : 'unsuspending'} the user`);
            App.telemetry.postUserTask("UpdateUserSuspensionState", TelemetryResult.Failure);
            return;

        // Get list of users for admin view
        case requestAction(Actions.SEARCH_USERS_FOR_ADMIN_VIEW):
            draftState.usersListForAdminView = {
                isFetching: true,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: 0,
                    items: []
                }
            } as FetchablePagedList<User>;
            return;

        case successAction(Actions.SEARCH_USERS_FOR_ADMIN_VIEW):
            draftState.usersListForAdminView = {
                isFetching: false,
                hasErrors: false,
                data: {
                    pageNumber: actionAny.__rest.currentPage,
                    pageSize: actionAny.__rest.pageSize,
                    totalItemsCount: actionAny.req.data.totalItemsCount,
                    items: actionAny.req.data.items ?? []
                },
            } as FetchablePagedList<User>;

            App.telemetry.postOperation("SearchUsersForAdminView", TelemetryResult.Success);
            return;

        case failureAction(Actions.SEARCH_USERS_FOR_ADMIN_VIEW):
            draftState.usersListForAdminView = {
                isFetching: false,
                hasErrors: true,
            } as FetchablePagedList<User>;

            App.telemetry.postOperation("SearchUsersForAdminView", TelemetryResult.Failure);
            return;
    }
}, {} as AppState);

const keywords: string[] = [
    ResourceStrings.keywordCrash(),
    ResourceStrings.keywordFreeze(),
    ResourceStrings.keywordFrozen(),
    ResourceStrings.keywordHang(),
    ResourceStrings.keywordNotResponding(),
    ResourceStrings.keywordStuck(),
    ResourceStrings.keywordCpu(),

    // added 2019-04-04:
    ResourceStrings.keywordPerformance(),
    ResourceStrings.keywordSlow(),
    ResourceStrings.keywordRAM(),
    ResourceStrings.keywordMemory(),
    ResourceStrings.keywordDeadlock(),
    ResourceStrings.keywordLag(),
];

function containsRecordTipKeywords(text: string): boolean {
    text = text || '';
    text = text.toLocaleLowerCase();

    for (const keyword of keywords) {
        if ((text.indexOf(" " + keyword) >= 0) || (text.indexOf(keyword + " ") >= 0)) {
            return true;
        }
    }

    return false;
}

function shouldNextBeEnabled(state: AppState): boolean {
    if ((state.reportTitle == null) || (state.reportTitle === "") || (state.reportDetail == null) || (state.reportDetail === "")) {
        return false;
    }

    if ((state.reportTitle.length < 4) || (state.reportTitle.length > 1000) || (state.reportDetail.length < 4)) {
        return false;
    }

    return true;
}

// find all 'a' elements within 'raw' elements and ensure they have title/tooltip
// fixes: 367197 Show where the link links to when you hover over a weblink
function updateAnchorElementsTitle(): void {
    const raws: HTMLCollectionOf<Element> = document.getElementsByTagName('raw');

    for (let rawIndex = 0; rawIndex < raws.length; rawIndex++) {
        const raw: Element | null = raws.item(rawIndex);

        if (raw == null) {
            continue;
        }

        const elements: HTMLCollectionOf<HTMLAnchorElement> = raw.getElementsByTagName('a');
        for (let i = 0; i < elements.length; i++) {
            const element: HTMLAnchorElement | null = elements.item(i);
            if (element == null) {
                continue;
            }

            if (element.title !== element.href) {
                element.title = element.href;
            }
        }
    }
}

function determineTags(searchTags: TagsResult, suggestedTags: string[]): TagInfo[] {
    // order: <typed tags> <suggested tags not found in typed>
    const tags: TagInfo[] = [];
    // tslint:disable-next-line:prefer-object-spread
    const typedTagsDict: { [tagName: string]: boolean } = Object.assign({}, searchTags.tagsDict); // copy dictionary to avoid modifying input arguments

    // add typed tags
    searchTags.tags.forEach((tagName: string) => {
        tags.push({ text: tagName, isSelected: true });
    });

    // add suggested tags not found in typed
    suggestedTags.forEach((tagName: string) => {
        if (typedTagsDict[tagName.toLocaleLowerCase()] === undefined) { // if not typed tag
            tags.push({ text: tagName, isSelected: typedTagsDict[tagName.toLocaleLowerCase()] === true });
        }
    });

    return tags;
}

function addComment(rootPost: RootPost, currentUserId: PrincipalId, currentUserName: string, commentText: string): void {
    commentText = App.HtmlFromMarkdownIfNeeded(commentText);
    rootPost.children.push({
        id: -1 as PostId,
        author: {
            id: currentUserId,
            name: currentUserName,
        },
        createdDate: new Date(),
        text: commentText,
        children: [],
        parentId: rootPost.id,
        isClean: false,
        isDeleted: false,
        lastActivityDate: new Date(),
        permittedViewers: null,
        type: PostType.comment,
        reactCounts: ReactionCounts.zero(),
        ownReactCounts: ReactionCounts.zero(),
        tags: [],

        isPublic: true,
        recipientNames: "",
        canReactivate: false,
        couldReactivateEver: false,
        createdByImageLink: getProfileImageUrl(currentUserId),
        reaction: null,
        voteCount: 0,
        userVoteType: null,
        trackingId: null,
    });
}

function getProfileImageUrl(id: PrincipalId): string {
    if (id !== FeedbackBotId) {
        // For now client will use user's name to generate avatar except for special cases like Feedback Bot where we use a server side image
        return null;
    }

    return `https://aka.ms/${instance.isInternal ? "dcoxo" : "dc"}/avatar?userId=${id}`;
}

function addHttpsToUrl(alerts: Alert[]): Alert[] {
    for (let i = 0; i < alerts.length; i++) {
        if (alerts[i].url != null && !alerts[i].url.startsWith("https")) {
            alerts[i].url = "https://" + alerts[i].url;
        }
    }
    return alerts;
}

/** Updates post in-place to make sure all its data is valid/usable */
function computeRootPostData(post: RootPost): void {
    post.createdDate = App.parseDate(post.createdDate as any);

    const stateTag: Tag | null = post.tags.find(tag => tag.category === TagCategory.state);
    const spaceTag: Tag | null = post.tags.find(tag => tag.category === TagCategory.space);
    const userNames: { [id: number]: string } = getDefaultUserNames();
    userNames[post.author.id] = post.author.name;

    post.createdByImageLink = getProfileImageUrl(post.author.id);
    post.state = stateFromStateTagId(stateTag?.id, post.type);
    post.stateTagId = stateTag?.id ?? StateTag.unknown;
    post.iCreatedThis = post.currentUserId === post.author.id && post.currentUserId !== Users.Anonymous;
    post.createdByUrl = App.GetUserProfileLink(post.author.id);
    post.spaceId = spaceTag?.id ?? SpaceId.VS;
    post.text = App.HtmlFromMarkdownIfNeeded(post.text);

    if (post.ai?.summary) {
        post.ai.summary.text = App.HtmlFromMarkdownIfNeeded(post.ai.summary.text);
    }

    post.reactCounts = post.reactCounts ?? ReactionCounts.zero();
    post.ownReactCounts = post.ownReactCounts ?? ReactionCounts.zero();
    post.totalVotes = post.reactCounts[ReactionType.upvote] - post.reactCounts[ReactionType.downvote];
    post.hasVoted = post.ownReactCounts[ReactionType.upvote] !== 0 || post.ownReactCounts[ReactionType.downvote] !== 0

    // compute "can vote or comment?"
    const isClosed = isClosedState(post.state);
    post.cantVote = isClosed || post.iCreatedThis; // can't vote if they created post or in closed state
    post.cantComment = isClosed; // can't vote if post closed

    // compute comment count, children data, and which solution can be reactivated (if any)
    let lastOfficialSolution: ChildPost | null = null;
    let commentCount: number = 0;
    let solutionCount: number = 0;
    post.children.forEach(child => {
        const totals = computeChildPostData(child, post.currentUserId, userNames);
        solutionCount += totals.solutionCount;
        commentCount += totals.commentCount;
        child.canReactivate = false;
        child.couldReactivateEver = false;

        if (child.type === PostType.solution && hasSystemTag(child.tags, SystemTags.solution_official)) {
            child.couldReactivateEver = true;
            lastOfficialSolution = child;
        }
    });
    post.commentCount = commentCount;
    post.solutionCount = solutionCount;
    // update canReactivate if needed
    const ninetyDays = 90 * (1000 * 60 * 60 * 24);
    if (lastOfficialSolution && lastOfficialSolution.createdDate.getTime() > (new Date().getTime() - ninetyDays)) {
        // solution_official AND last solution?  (< 90d ? Allowed : Expired) : NotApplicable;
        lastOfficialSolution.canReactivate = true;
    }

    // compute history states - change filter as new history items are enabled
    // remove everything except state changes and auto tagging to prevent showing non-state tags as "Unknown" states
    post.partialHistory = post.partialHistory.filter(item =>
        (item.action === PostAction.add_tag && isStateTag(item.tagId)) ||
        (item.action === PostAction.auto_tag) ||
        (item.action === PostAction.route)
    );
    post.partialHistory.forEach(item => {
        item.date = App.parseDate(item.date as any);
        if (item.action === PostAction.add_tag && isStateTag(item.tagId)) {
            item.status = stateFromStateTagId(item.tagId, post.type);
        }
    });
}

function computeChildPostData(post: ChildPost, currentUserId: PrincipalId, userNames: { [id: number]: string }): { commentCount: number, solutionCount: number } {
    post.createdDate = App.parseDate(post.createdDate as any);
    post.createdByImageLink = getProfileImageUrl(post.author.id);
    userNames[post.author.id] = post.author.name;

    // "visible to" list of names
    post.recipientNames = "";
    if (!post.isPublic) {
        const len: number = post.permittedViewers?.length ?? 0;
        let isAuthorFound: boolean = false;
        for (let i = 0; i < len; i++) {
            const viewer: PrincipalId = post.permittedViewers[i];
            if (viewer === post.author.id) {
                isAuthorFound = true;
            }
            post.recipientNames += userNames[viewer] ?? viewer;
            if (i < len - 1) {
                post.recipientNames += ", ";
            }
        }

        if (!isAuthorFound) {
            post.recipientNames = post.author.name + ", " + post.recipientNames;
        }
    }

    post.reactCounts = post.reactCounts ?? ReactionCounts.zero();
    post.ownReactCounts = post.ownReactCounts ?? ReactionCounts.zero();
    post.voteCount = post.reactCounts[ReactionType.upvote] - post.reactCounts[ReactionType.downvote];

    const ownVoteCount: number = post.ownReactCounts[ReactionType.upvote] - post.ownReactCounts[ReactionType.downvote];
    post.userVoteType = ownVoteCount === 0 ? null : (ownVoteCount > 0 ? "voteup" : "votedown");

    // compute reaction
    post.reaction = null;
    for (const tag of post.tags) {
        if (tag.id === SystemTags.comment_react_yes) {
            post.reaction = "reactYes";
            break;
        }

        if (tag.id === SystemTags.comment_react_no) {
            post.reaction = "reactNo";
            break;
        }
    }

    let commentCount: number = 0;
    let solutionCount: number = 0;
    if ((post.type === PostType.solution || post.type === PostType.workaround) && !post.isDeleted) {
        solutionCount++;
    }
    else if (post.type === PostType.comment && !post.isDeleted) {
        commentCount++;
    }
    post.text = App.HtmlFromMarkdownIfNeeded(post.text);
    post.children.forEach(child => {
        const totals = computeChildPostData(child, currentUserId, userNames);
        solutionCount += totals.solutionCount;
        commentCount += totals.commentCount;
    });

    return { commentCount, solutionCount };
}

function getSearchInfoFromState(state: AppState, target: SearchInfoTarget): SearchInfo {
    switch (target) {
        case SearchInfoTarget.problems:
            return state.problems;

        case SearchInfoTarget.suggestions:
            return state.suggestions;

        case SearchInfoTarget.myProblems:
            return state.myProblems;

        case SearchInfoTarget.mySuggestions:
            return state.mySuggestions;

        case SearchInfoTarget.myNMIItems:
            return state.myNMIItems;

        case SearchInfoTarget.all:
            return state.problems;

        default:
            throw new Error("Unknown SearchInfo: " + target);
    }
}

function setSearchInfo(state: AppState, info: SearchInfo, target: SearchInfoTarget): void {
    switch (target) {
        case SearchInfoTarget.problems:
            state.problems = info;
            break;

        case SearchInfoTarget.suggestions:
            state.suggestions = info;
            break;

        case SearchInfoTarget.myProblems:
            state.myProblems = info;
            break;

        case SearchInfoTarget.mySuggestions:
            state.mySuggestions = info;
            break;

        case SearchInfoTarget.myNMIItems:
            state.myNMIItems = info;
            break;


        case SearchInfoTarget.all:
            // @TODO: Simplify the state to match Devcom2
            state.problems = info;
            break;

        default:
            throw new Error("Unknown SearchInfo: " + target);
    }
}

function addVoteIfNeeded(results: SearchResult[], voteForTrackingId: string): void {
    for (const i in results) {
        // eslint-disable-next-line security/detect-object-injection
        const item: SearchResult = results[i];
        if ((item.trackingId === voteForTrackingId) && !item.hasVoted) {
            // WARNING! Required due to potential bug in react where react will freeze properties - make them readonly on vote
            // eslint-disable-next-line security/detect-object-injection
            results[i] = { ...item };

            // eslint-disable-next-line security/detect-object-injection
            results[i].hasVoted = true;
            // eslint-disable-next-line security/detect-object-injection
            results[i].totalVotes++;
            break;
        }
    }
}

// hack to make sure NVDA/screen readers read the "signing in" text
function focusSignInNoteIfNeeded(state: AppState): void {
    if ((state.signInState === SignInState.Success) || (state.signInState === SignInState.Prompt)) {
        return;
    }

    const elemId: string = state.view === MainView.home ? "signInNote_home" : "signInNote_search";
    showAsBlockAndFocusElement(elemId);
}

function showAsBlockAndFocusElement(elemId: string): void {
    const element: HTMLElement | null = document.getElementById(elemId);
    if (element == null) {
        return;
    }

    element.style.display = "block";
    element.focus();
}

function focusElement(elemId: string): void {
    const element: HTMLElement | null = document.getElementById(elemId);
    if (element == null) {
        return;
    }

    element.focus();
}

/** Marks feedback as voted, updates all system references to it and shows success message */
function markVotedAndShowSuccess(postId: PostId, voteForTrackingId: string | null, draftState: AppState, operationKey: string | null, successMessage: string) {
    if (voteForTrackingId == null || voteForTrackingId === "") {
        return;
    }

    if (draftState.selectedItem != null &&
        draftState.selectedItem.trackingId === voteForTrackingId &&
        !draftState.selectedItem.hasVoted) {
        draftState.selectedItem.hasVoted = true;
        draftState.selectedItem.totalVotes++;
    }
    addVoteIfNeeded(App.GetActiveSearchInfo(draftState).results.results, voteForTrackingId);
    addVoteIfNeeded(draftState.myProblems.results.results, voteForTrackingId);
    addVoteIfNeeded(draftState.myActivity.results.results, voteForTrackingId);

    if (App.hasVotedCache[voteForTrackingId] !== true) {
        // eslint-disable-next-line security/detect-object-injection
        App.hasVotedCache[voteForTrackingId] = true;
    }

    const ticket: Fetchable<RootPost> | null = draftState.ticketsById[postId];
    if (ticket?.data && ticket.data.hasVoted !== true) {
        ticket.data.hasVoted = true;
        ticket.data.totalVotes++;
        ticket.data.ownReactCounts[ReactionType.upvote]++;
        ticket.data.reactCounts[ReactionType.upvote]++;
    }

    if (operationKey) {
        operationSuceeded(draftState, operationKey, successMessage);
    }
}

function removeVoteFromSearchInfo(searchResult: SearchResult[], rootPostId: RootPostId) {
    for (const i in searchResult) {
        const item: SearchResult = searchResult[i];
        if (item.id === rootPostId) {
            searchResult[i] = { ...item };
            searchResult[i].hasVoted = false;
            searchResult[i].totalVotes--;
            break;
        }
    }
}

function markVoteForNode(rootPostId: RootPostId, postId: PostId, draftState: AppState, voteType: ReactionType, isCancelled: boolean) {
    const ticket: Fetchable<RootPost> | null = draftState.ticketsById[rootPostId];
    if (!ticket?.data && isCancelled) {
        removeVoteFromSearchInfo(App.GetActiveSearchInfo(draftState).results.results, rootPostId);
        removeVoteFromSearchInfo(draftState.myProblems.results.results, rootPostId);
        removeVoteFromSearchInfo(draftState.myActivity.results.results, rootPostId);
        return;
    }

    if (rootPostId === postId) {
        // This shouldn't be an issue anymore,
        // // but keeping it here just in case we run into it again.
        if (!ticket?.data) {
            console.error("No ticket data found for root post ID: " + rootPostId);
            return;
        }

        if (ticket.data.hasVoted && isCancelled) {
            ticket.data.hasVoted = false;
            ticket.data.totalVotes--;
            ticket.data.ownReactCounts[voteType]--;
            ticket.data.reactCounts[voteType]--;
        }
    } else {
        const childPost: ChildPost | null = findChildById(ticket.data.children, postId);
        // same for childPost, I don't think this will happen.
        // but adding this check just in case we have an error.
        if (!childPost) {
            return;
        }

        if (isCancelled) {
            childPost.ownReactCounts[voteType]--;
            childPost.reactCounts[voteType]--;
            childPost.voteCount = childPost.reactCounts[ReactionType.upvote] - childPost.reactCounts[ReactionType.downvote];
            childPost.userVoteType = null

        } else {
            const currentVoteType = voteType === ReactionType.upvote ? "voteup" : "votedown";
            // this is for when user already voteup for a post, but decided to change their reaction to vote down
            // we would need to cancel their voteup before mark their vote down.
            if (childPost.userVoteType !== null && childPost.userVoteType !== currentVoteType) {
                const userVoteReactionType = childPost.userVoteType === "voteup" ? ReactionType.upvote : ReactionType.downvote;
                childPost.ownReactCounts[userVoteReactionType]--;
                childPost.reactCounts[userVoteReactionType]--;
            }
            childPost.ownReactCounts[voteType]++;
            childPost.reactCounts[voteType]++;
            childPost.voteCount = childPost.reactCounts[ReactionType.upvote] - childPost.reactCounts[ReactionType.downvote];
            childPost.userVoteType = currentVoteType;
        }
    }
}

function findChildById(children: ChildPost[], postId: number): ChildPost | null {
    for (const child of children) {
        if (child.id === postId) {
            return child;
        }
        if (child.children && child.children.length > 0) {
            const found = findChildById(child.children, postId);
            if (found) {
                return found;
            }
        }
    }
    return null;
}

function resetScreenRecordingState(draftState: AppState): void {
    draftState.reportIncludeScreenRecording = false;
    draftState.screenRecording = null;
    draftState.screenRecordingUploadState = OperationState.NotStarted;
    draftState.screenRecordingErrorMessage = null;
    draftState.screenRecordingPercentDone = 0;
}

/** 
 * A/B test function to determine if user is in test group
 */
function abTest(userId: PrincipalId, isAdminOrModerator: boolean): boolean {
    // if admin/mod always in test group
    if (isAdminOrModerator) {
        return true;
    }

    // even userIds are in test group
    return userId % 2 === 0;
}